import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";

interface AuthenticationCreateAccountStepButtonsProps {
  position: "first" | "middle" | "last";
  onNext: () => void | Promise<void>;
  onPrevious: () => void;
  loading: boolean;
  disabled: boolean;
}

// Three layouts, all at the large size so the buttons don't change size from
// step to step:
//  - first — Next alone, full width. There is no Previous to go back to.
//  - middle — Previous and Next split the row into two equal halves, so they're
//    the same size whatever their labels.
//  - last — one row where Previous shrinks to its label and the submit takes
//    the rest, since its label is much longer. Below the `sm` breakpoint that
//    row can't fit (button labels never wrap), so it stacks with the submit on
//    top.
export function AuthenticationCreateAccountStepButtons(
  props: AuthenticationCreateAccountStepButtonsProps,
) {
  const { position, onNext, onPrevious, loading, disabled } = props;
  const isLast = position === "last";

  const nextButton = (
    <Button
      primary
      size="large"
      fullWidth
      nextArrow={!isLast}
      onClick={onNext}
      loading={loading}
      disabled={disabled}
      disabledMessage="Complete the required fields above to continue"
      data-testid="create-account-next-button"
    >
      {isLast ? "Create Profile and Get Your Quotes" : "Next"}
    </Button>
  );

  const previousButton = (
    <Button
      size="large"
      fullWidth={!isLast}
      previousArrow
      onClick={onPrevious}
      data-testid="create-account-previous-button"
    >
      Previous
    </Button>
  );

  if (position === "first") {
    return nextButton;
  }

  if (position === "middle") {
    return (
      <Grid columns={2} gap="sm">
        {previousButton}
        {nextButton}
      </Grid>
    );
  }

  return (
    <div className="flex flex-col-reverse items-center gap-4 sm:flex-row">
      {previousButton}
      <div className="w-full sm:flex-1">{nextButton}</div>
    </div>
  );
}
